import {
  config,
  startAnalytics,
  stopAnalytics,
  trackView,
} from '@formue-app/core';

import { resolveRoutePattern } from './routePatterns';

// The web side of the shared tracker in core: route patterns, browser context
// and the pathname dedupe. Everything else (session, ids, referrer/page, the
// send) lives in core/src/services/analytics.js.

// The mobile app WebView and the backend's headless PDF renders boot with the
// token (or, for an /oidc PDF render, a login assertion) in the hash. Both are
// captured here because the hash is gone by the time analytics starts; neither
// is tracked.
const bootedFromTokenHash = /(access_token|login_assertion)=/.test(
  window.location.hash
);

// Mirrors core's started state so a route change before init doesn't consume
// the landing pathname in the dedupe below without sending anything.
let started = false;
let lastTrackedPathname = null;

const getContext = () => ({
  userAgent: navigator.userAgent,
  locale: navigator.language,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  viewport: `${window.innerWidth}x${window.innerHeight}`,
  appVersion: config.app.appVersion,
});

/**
 * Track a route change as a page_view, named by route pattern. No-op before
 * init, on public/blacklisted/unmatched routes, and on a repeated pathname.
 */
export const trackPageView = (pathname) => {
  try {
    if (!started || pathname === lastTrackedPathname) return;

    const resolved = resolveRoutePattern(pathname);
    if (!resolved) return;

    const { pattern, resourceId, params } = resolved;
    const properties = {};
    if (params) {
      properties.params = params;
    }

    lastTrackedPathname = pathname;
    trackView(pattern, { resourceId, properties });
  } catch {
    // Tracking a route must never break navigating to it.
  }
};

/**
 * Start tracking and record the landing route. Called from the init and
 * impersonate sagas, after any impersonation header is set; idempotent.
 */
export const initAnalytics = () => {
  try {
    if (started || bootedFromTokenHash) return;
    started = true;
    startAnalytics({
      client: 'web',
      getContext,
      storage: window.sessionStorage,
    });
    trackPageView(window.location.pathname);
  } catch {
    // Analytics must never break app init.
  }
};

/**
 * Logout (incl. idle timeout): stop tracking and drop the session, so nothing
 * is sent until the next login runs initAnalytics again.
 */
export const resetAnalytics = () => {
  started = false;
  lastTrackedPathname = null;
  stopAnalytics();
};
